import { fetchAPI, IParam } from 'libs/apis'
import useAxios from 'axios-hooks'
import type { UseAxiosResult } from 'axios-hooks'
import { AxiosRequestConfig } from 'axios'
import i18n from 'config/i18n'

import { Company, CompanyForFindOne, CompanyForFormSubmit, STATUS } from 'types'
import { LIMIT_PAGE } from 'helpers/constants'
import { IParamForSubmit } from 'libs/apis/helper'

const appendForm = <T>(form: FormData, data: T) => {
  Object.entries(data).map(async ([key, value]) => {
    if (key === 'logo' && typeof value === 'string') {
      return
    }

    if (key === 'logo') {
      form.append(key, value, value.name)

      return
    }

    const isAddressField = [
      'country',
      'province',
      'district',
      'subDistrict',
    ].some((_key) => _key === key)

    if (isAddressField) {
      Object.entries(value).forEach(([_key, _value]) => {
        if (_key === 'id') {
          return
        }

        form.append(_key, _value as string)
      })

      return
    }

    form.append(key, value)
  })

  return form
}

export const findAll = async (
  params: IParam & { name?: string; taxId?: string },
  options?: AxiosRequestConfig,
): Promise<{ body: Company[]; totals: number }> => {
  const withDefaultParams: IParamForSubmit = {
    _limit: LIMIT_PAGE,
    searchColumns: [
      'name',
      'code',
      'contacts.number',
      'addresses.provinceTh',
      'addresses.districtTh',
      'addresses.subDistrictTh',
      'addresses.address',
    ],
    ...params,
  }

  if (withDefaultParams?.isActive === STATUS.ALL) {
    delete withDefaultParams.isActive
  }

  const newParam = {
    ...withDefaultParams,
    name: params.name?.trim(),
  }

  const companies = await fetchAPI({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1.2/company',
    params: newParam,
    options,
  })

  if (companies.status !== 200) {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return {
    body: companies.body as Company[],
    totals: +companies.headers['x-total-count'] || 0,
  }
}

export const useFindAll = (params?: IParam): UseAxiosResult<Company[]> => {
  const path = `${window._env_.REACT_APP_API_URL}/v1/company`
  const nextParams = {
    _limit: '0',
    ...params,
  }

  return useAxios({
    url: path,
    params: nextParams,
  })
}

export const findOneById = async (id: string): Promise<CompanyForFindOne> => {
  const company = await fetchAPI<CompanyForFindOne>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: `/v1.2/company/${id}`,
  })

  if (company.status !== 200) {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return company.body
}

export const create = async (
  body: CompanyForFormSubmit,
  handleOpenSuccess: () => void,
): Promise<Company> => {
  const form = new FormData()
  const appendedForm = appendForm<CompanyForFormSubmit>(form, body)
  const response = await fetchAPI<Company>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'POST',
    path: '/v1.2/company',
    options: {
      headers: { 'Content-Type': 'multipart/form-data' },
      data: appendedForm,
    },
  })

  const isSuccess = response.status === 200 || response.status === 201
  if (isSuccess) {
    handleOpenSuccess()
  } else {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return response.body
}

export const update = async (
  id: string,
  body: CompanyForFormSubmit,
  handleOpenSuccess: () => void,
): Promise<Company> => {
  const form = new FormData()
  const appendedForm = appendForm<CompanyForFormSubmit>(form, body)

  const response = await fetchAPI<Company>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'PATCH',
    path: `/v1.2/company/${id}`,
    options: {
      headers: { 'Content-Type': 'multipart/form-data' },
      data: appendedForm,
    },
  })

  const isSuccess = response.status === 200 || response.status === 201
  if (isSuccess) {
    handleOpenSuccess()
  } else {
    throw new Error(i18n.t('error:notFound.title'))
  }

  return response.body
}

export const downloadCompanyTemplate = async (): Promise<Blob> => {
  const response = await fetchAPI<{
    arrayBuffer(): Blob
  }>({
    apiUrl: window._env_.REACT_APP_API_URL,
    method: 'GET',
    path: '/v1.2/company/import/template',
    responseType: 'blob',
  })

  return response.body.arrayBuffer()
}
